/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ErrorModel } from '@/shared/api/BaseAPI.model'
import { useTranslation } from 'react-i18next'
import { IngredientPayloadModel, QUERY } from '../api/IngredientsAPI.model'
import IngredientsAPI from '../api/IngredientsAPI'
import { useSnackbar } from '@/shared/components/Snackbar/SnackbarProvider'

export function useIngredientsQuery(ingredientId?: string) {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const { showSnackbar } = useSnackbar()
  const allergensList = useQuery({
    queryKey: [QUERY.ALLERGENS],
    queryFn: () => IngredientsAPI.getAllergens()
  })

  const unitsList = useQuery({
    queryKey: [QUERY.UNITS],
    queryFn: () => IngredientsAPI.getUnits()
  })

  const dietsList = useQuery({
    queryKey: [QUERY.DIETS],
    queryFn: () => IngredientsAPI.getDiets()
  })

  const { data: ingredientsList, isLoading: ingredientListLoading } = useQuery({
    queryKey: [QUERY.INGREDIENTS],
    queryFn: () => IngredientsAPI.getIngredients()
  })

  const { mutate: postIngredient } = useMutation<
    IngredientPayloadModel,
    ErrorModel,
    any
  >({
    mutationFn: (ingredientPayload: IngredientPayloadModel) =>
      IngredientsAPI.postIngredient(ingredientPayload),
    onSuccess: () => {
      showSnackbar(
        t('ALERTS.POST_INGREDIENT_SUCCESS.TITLE'),
        t('ALERTS.POST_INGREDIENT_SUCCESS.DESCRIPTION'),
        'success'
      )
      queryClient.invalidateQueries({ queryKey: [QUERY.INGREDIENTS] })
    },
    onError: () => {
      showSnackbar(
        t('ALERTS.POST_INGREDIENT_ERROR.TITLE'),
        t('ALERTS.POST_INGREDIENT_ERROR.DESCRIPTION'),
        'error'
      )
    }
  })

  const { mutate: putIngredient } = useMutation<
    IngredientPayloadModel,
    ErrorModel,
    any
  >({
    mutationFn: (ingredientPayload: IngredientPayloadModel) =>
      IngredientsAPI.putIngredient(ingredientId!, ingredientPayload),
    onSuccess: () => {
      showSnackbar(
        t('ALERTS.PUT_INGREDIENT_SUCCESS.TITLE'),
        t('ALERTS.PUT_INGREDIENT_SUCCESS.DESCRIPTION'),
        'success'
      )
      queryClient.invalidateQueries({ queryKey: [QUERY.INGREDIENTS] })
    },
    onError: () => {
      showSnackbar(
        t('ALERTS.PUT_INGREDIENT_ERROR.TITLE'),
        t('ALERTS.PUT_INGREDIENT_ERROR.DESCRIPTION'),
        'error'
      )
    }
  })

  const { mutate: deleteIngredients } = useMutation<void, ErrorModel, string[]>(
    {
      mutationFn: (ingredientIds: string[]) =>
        IngredientsAPI.deleteIngredients(ingredientIds),
      onSuccess: () => {
        showSnackbar(
          t('ALERTS.DELETE_INGREDIENTS_SUCCESS.TITLE'),
          t('ALERTS.DELETE_INGREDIENTS_SUCCESS.DESCRIPTION'),
          'success'
        )
        queryClient.invalidateQueries({ queryKey: [QUERY.INGREDIENTS] })
      },
      onError: () => {
        showSnackbar(
          t('ALERTS.DELETE_INGREDIENTS_ERROR.TITLE'),
          t('ALERTS.DELETE_INGREDIENTS_ERROR.DESCRIPTION'),
          'error'
        )
      }
    }
  )

  return {
    allergensList,
    unitsList,
    dietsList,
    ingredientsList,
    ingredientListLoading,
    postIngredient,
    putIngredient,
    deleteIngredients
  }
}
